import React from 'react';
import { motion } from 'framer-motion';
import { WhatsappLogo, Link as LinkIcon, EnvelopeSimple, Copy, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const ShareGiftCard = ({ giftCode, amount, recipientName, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  
  const shareUrl = `${window.location.origin}/gift?code=${giftCode}`;
  const shareText = `I sent you a £${amount} habitué gift card! 🎁 Use it at any merchant in the network.`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    window.open(url, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`You received a £${amount} habitué gift card!`);
    const body = encodeURIComponent(shareText + '\n\n' + shareUrl);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'habitué Gift Card',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Share failed');
        }
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#052e16]/80 backdrop-blur-sm flex items-center justify-center z-50 px-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gradient-to-br from-[#052e16] to-[#1F2937] border border-green-700 rounded-2xl p-8 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-2xl tracking-tight text-white mb-2">Share Gift Card</h3>
        <p className="text-green-300 mb-6">Let {recipientName || 'them'} know about their gift!</p>

        {/* Share options */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <motion.button
            onClick={handleWhatsApp}
            className="flex flex-col items-center gap-2 p-4 bg-green-900/50 border border-green-700 rounded-xl hover:bg-green-800/50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <WhatsappLogo size={28} className="text-green-400" weight="fill" />
            <span className="text-xs text-green-300">WhatsApp</span>
          </motion.button>

          <motion.button
            onClick={handleEmail}
            className="flex flex-col items-center gap-2 p-4 bg-green-900/50 border border-green-700 rounded-xl hover:bg-green-800/50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <EnvelopeSimple size={28} className="text-blue-400" weight="fill" />
            <span className="text-xs text-green-300">Email</span>
          </motion.button>

          {navigator.share && (
            <motion.button
              onClick={handleNativeShare}
              className="flex flex-col items-center gap-2 p-4 bg-green-900/50 border border-green-700 rounded-xl hover:bg-green-800/50 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LinkIcon size={28} className="text-purple-400" weight="fill" />
              <span className="text-xs text-green-300">More</span>
            </motion.button>
          )}
        </div>

        {/* Copy link */}
        <div className="flex items-center gap-2 bg-green-900/50 border border-green-700 rounded-xl p-3">
          <input
            type="text"
            value={shareUrl}
            readOnly
            className="flex-1 bg-transparent text-gray-300 text-sm outline-none truncate"
          />
          <button
            onClick={handleCopy}
            className="flex items-center gap-1 text-green-300 hover:text-white px-3 py-1 rounded-lg transition-colors"
          >
            {copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 bg-green-900/50 border border-green-700 text-white py-3 rounded-xl font-medium hover:bg-green-800/50 transition-colors"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ShareGiftCard;
